// libraries
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

type AuthEntryKind = 'wellKnown' | 'iframe' | 'token' | 'keys';

type AuthKindSpan = { count: number; startMs: number; endMs: number };

const AUTH_HOST = 'authorize.coxautoinc.com';

const COUNT_ATTRIBUTE: Record<AuthEntryKind, string> = {
    wellKnown: 'wellKnownCount',
    iframe: 'iframeCount',
    token: 'tokenPostCount',
    keys: 'keysCount'
};

const roundMs = (value: number): number => Math.round(value * 10) / 10;

const classifyAuthEntry = (entry: PerformanceResourceTiming): AuthEntryKind | undefined => {
    if (entry.initiatorType === 'iframe') return 'iframe';
    if (entry.name.includes('.well-known/openid-configuration')) return 'wellKnown';
    if (entry.name.includes('/v1/token')) return 'token';
    if (entry.name.includes('/v1/keys')) return 'keys';
    return undefined;
};

const guard = (action: () => void): void => {
    try {
        action();
    } catch {
        return;
    }
};

let pageActionName: string | undefined;
let observer: PerformanceObserver | undefined;
let collected: PerformanceResourceTiming[] = [];
let gateStartMs: number | undefined;
let initDispatchMs: number | undefined;
let reported = false;

const collect = (entries: PerformanceEntryList): void => {
    entries.forEach((entry) => {
        if (entry.name.includes(AUTH_HOST)) collected.push(entry as PerformanceResourceTiming);
    });
};

const buildAttributes = (gateStart: number, initDispatch: number): Record<string, number | boolean> => {
    const spans: Partial<Record<AuthEntryKind, AuthKindSpan>> = {};
    let authFirstStartMs: number | undefined;
    let authLastEndMs: number | undefined;
    let renewalRan = false;

    collected.forEach((entry) => {
        const kind = classifyAuthEntry(entry);
        if (!kind) return;

        const span = spans[kind];
        spans[kind] = {
            count: (span?.count ?? 0) + 1,
            startMs: Math.min(span?.startMs ?? entry.startTime, entry.startTime),
            endMs: Math.max(span?.endMs ?? entry.responseEnd, entry.responseEnd)
        };
        authFirstStartMs = Math.min(authFirstStartMs ?? entry.startTime, entry.startTime);
        authLastEndMs = Math.max(authLastEndMs ?? entry.responseEnd, entry.responseEnd);

        if ((kind === 'token' || kind === 'iframe') && entry.responseEnd >= gateStart) renewalRan = true;
    });

    const attributes: Record<string, number | boolean> = {
        renewalRan,
        gateStartMs: roundMs(gateStart),
        initDispatchMs: roundMs(initDispatch),
        blockedMs: roundMs(initDispatch - gateStart)
    };

    (Object.keys(COUNT_ATTRIBUTE) as AuthEntryKind[]).forEach((kind) => {
        const span = spans[kind];
        attributes[COUNT_ATTRIBUTE[kind]] = span?.count ?? 0;
        if (span) {
            attributes[`${kind}StartMs`] = roundMs(span.startMs);
            attributes[`${kind}EndMs`] = roundMs(span.endMs);
        }
    });

    if (authFirstStartMs !== undefined && authLastEndMs !== undefined) {
        attributes.authFirstStartMs = roundMs(authFirstStartMs);
        attributes.authLastEndMs = roundMs(authLastEndMs);
    }

    return attributes;
};

export const startAuthGateTelemetry = (config: { pageActionName: string }): void => {
    pageActionName = config.pageActionName;
    guard(() => {
        observer = new PerformanceObserver((list) => collect(list.getEntries()));
        observer.observe({ type: 'resource', buffered: true });
    });
};

export const markAuthGateStart = (): void => {
    gateStartMs ??= performance.now();
};

export const markInitDispatch = (): void => {
    initDispatchMs ??= performance.now();
};

export const reportAuthGate = (): void => {
    const name = pageActionName;
    const gateStart = gateStartMs;
    const initDispatch = initDispatchMs;
    if (reported || !name || gateStart === undefined || initDispatch === undefined) return;
    reported = true;

    guard(() => {
        collect(observer?.takeRecords() ?? []);
        logNewRelicPageAction(name, buildAttributes(gateStart, initDispatch));
    });
    guard(() => observer?.disconnect());
    observer = undefined;
    collected = [];
};

export const resetAuthGateTelemetryForTests = (): void => {
    observer?.disconnect();
    observer = undefined;
    collected = [];
    pageActionName = undefined;
    gateStartMs = undefined;
    initDispatchMs = undefined;
    reported = false;
};
